import React, { useState, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { withDevice } from '@catalogo/core-connect/device';
import Close from '@catalogo/ui-americanas-desktop-svg/close.svg';
import { IProductTooltip, IContentContainer, IInvisibleWrapper } from './helpers/interfaces';
import { handleOnClickCapture, showTooltip, closeTooltip, mouseLeaveTooltip, toggleTooltip } from './helpers/functions';

export const ProductTooltip: React.FC<IProductTooltip> = ({
  children,
  device,
  openTooltipWith,
  startOpen,
  nodeElementCloseOnClick = 'a',
  arrowMarginLeft = '10px',
  boxMarginLeft = '0px',
  tooltipSizeMobile = '338px',
  tooltipSizeDesk = '410px',
  tooltipPosition = 'below',
  tooltipDistanceTop = '-88px',
  tooltipDistanceBottom = '22px',
}) => {
  const [visible, setVisible] = useState(startOpen);
  const timerRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (startOpen) {
      timerRef.current = setTimeout(closeTooltip(setVisible), 5000);
      return () => clearTimeout(timerRef.current as NodeJS.Timeout);
    }
  }, []);

  const [badge, content] = children;
  const mobileEvents = { onClick: toggleTooltip(visible, setVisible, openTooltipWith) };
  const desktopEvents = {
    onMouseEnter: showTooltip(setVisible, timerRef, openTooltipWith),
    onMouseLeave: mouseLeaveTooltip(setVisible, timerRef),
  };
  const wrapperEventProps = device === 'desktop' ? { ...desktopEvents } : {};
  const badgeEventProps = device === 'mobile' ? { ...mobileEvents } : {};

  const ContentContainerProps = {
    arrowMarginLeft,
    boxMarginLeft,
    tooltipSizeMobile,
    tooltipSizeDesk,
    tooltipPosition,
    device,
    onClickCapture: (e: unknown) => handleOnClickCapture(e, setVisible, nodeElementCloseOnClick),
  };

  const InvisibleWrapperProps = {
    tooltipPosition,
    tooltipDistanceTop,
    tooltipDistanceBottom,
  };

  return (
    <Container {...wrapperEventProps} className="product-tooltip">
      <BadgeContainer {...badgeEventProps} className="product-tooltip-badge-container">
        {badge}
      </BadgeContainer>
      {visible && (
        <InvisibleWrapper {...InvisibleWrapperProps} className="product-tooltip-invisible">
          <ContentContainer {...ContentContainerProps} className="product-tooltip-invisible-container">
            <CloseTooltip onClick={closeTooltip(setVisible)} className="product-tooltip-invisible-close">
              <CloseIcon className="product-tooltip-invisible-close-icon" />
            </CloseTooltip>
            {content}
          </ContentContainer>
        </InvisibleWrapper>
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
`;

const BadgeContainer = styled.div`
  display: table;
  cursor: default;
`;

const InvisibleWrapper = styled.div<IInvisibleWrapper>`
  ${({ tooltipPosition, tooltipDistanceTop, tooltipDistanceBottom }) => css`
    position: absolute;
    background: transparent;
    padding-top: 12px;
    z-index: 9;
    top: ${tooltipPosition === 'below' ? tooltipDistanceBottom : tooltipDistanceTop};
  `}
`;

const ContentContainer = styled.div<IContentContainer>`
  ${({ theme, boxMarginLeft, device, tooltipSizeDesk, tooltipSizeMobile, tooltipPosition, arrowMarginLeft }) => css`
    margin-left: ${boxMarginLeft};
    background: ${theme.white};
    border-radius: 4px;
    padding: 18px 16px 16px 16px;
    box-shadow: 0px 0px 6px ${theme.grey.medium};
    margin-bottom: ${tooltipPosition === 'below' ? '-2px' : '28px'};
    width: ${device === 'mobile' ? tooltipSizeMobile : tooltipSizeDesk};

    &:after {
      content: ' ';
      position: absolute;
      width: 0;
      height: 0;
      left: auto;
      top: ${tooltipPosition === 'below' ? '-33px' : '28px'};
      margin-left: ${arrowMarginLeft};
      margin-top: 40px;
      border: 5px solid;
      border-color: ${theme.white} transparent transparent ${theme.white};
      transform: ${tooltipPosition === 'below' ? 'rotate(45deg)' : 'rotate(225deg)'};
      box-shadow: -2px -2px 3px ${theme.grey.light};
    }
  `}
`;

const CloseTooltip = styled.div`
  position: absolute;
  top: 18px;
  right: 7px;
  cursor: pointer;
`;

const CloseIcon = styled(Close)`
  height: 18px;
  width: 18px;
  fill: ${({ theme }) => theme.grey.darker};
`;

export default withDevice(ProductTooltip);
